import {
  type ProductVariant,
  type Cart,
} from "@shopify/hydrogen-react/storefront-api-types";

import { type DocumentNode } from "graphql";

import getCartUpsellsQuery from "@graphql/get-data-api-cart-upsells.graphql";
import {
  LEGACY_CART_API_URL,
  STOREFRONT_ACCESS_TOKEN,
  YOTPO_REVIEWS_API_URL,
} from "@tablescape/constants";
import { captureException, getFullyQualifiedUrl } from "@tablescape/utilities";

export type UpsellDataAPIConnection = {
  data: {
    cartUpsells: {
      full_product_name: string;
      lift: number | null;
      variant_id: number;
    }[];
  };
};

export const dataAPIFetcher = async ([url, cartId, variantIds]: [
  string,
  Cart["id"],
  number[]
]) => {
  let resolvedVariantIds = variantIds;

  if (process.env.STOREFRONT_ENV === "staging") {
    const productIdMap = await import("@root/scripts/product-id-map.json");

    resolvedVariantIds = variantIds.map((variantId) => {
      const stagingVariantId = String(
        variantId
      ) as keyof typeof productIdMap.staging.variants;

      return (
        productIdMap.staging.variants[stagingVariantId]?.destinationId ||
        variantId
      );
    });
  }

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: getCartUpsellsQuery,
      variables: { cartId, cartLines: resolvedVariantIds },
    }),
    mode: "cors",
    credentials: "same-origin",
  })
    .then<UpsellDataAPIConnection>((res) => res.json())
    .then(async (res) => {
      if (process.env.STOREFRONT_ENV === "staging") {
        const productIdMap = await import("@root/scripts/product-id-map.json");

        return {
          ...res,
          data: {
            ...res.data,
            cartUpsells: res.data.cartUpsells.map((upsell) => {
              const productionVariantId = String(
                upsell.variant_id
              ) as keyof typeof productIdMap.production.variants;

              return {
                ...upsell,
                variant_id:
                  productIdMap.production.variants[productionVariantId]
                    ?.destinationId || upsell.variant_id,
              };
            }),
          },
        };
      }

      return res;
    })
    .catch((err) =>
      captureException(err, {
        tags: {
          "issue.type": "fetch",
        },
      })
    );
};

type StorefrontAPIParameters = [
  string,
  DocumentNode,
  Record<string, string | string[] | { key: string; value: string }[]>
];

export const storefrontAPIFetcher = async ([
  url,
  query,
  variables,
]: StorefrontAPIParameters) =>
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  })
    .then((res) => res.json())
    .catch((err) =>
      captureException(err, {
        tags: {
          "issue.type": "fetch",
        },
      })
    );

export const multiStorefrontAPIFetcher = (
  paramsArray: StorefrontAPIParameters[]
) => {
  return Promise.all(paramsArray.map((params) => storefrontAPIFetcher(params)));
};

type YotpoReviewsAPIParameters = [string, DocumentNode, Record<string, string>];

export const yotpoReviewsAPIFetcher = async ([
  appKey,
  productId,
  params = {},
]: YotpoReviewsAPIParameters) =>
  fetch(
    getFullyQualifiedUrl(
      `${YOTPO_REVIEWS_API_URL}/${appKey}/products/${productId}/reviews.json`,
      params
    )
  )
    .then((res) => res.json())
    .catch((err) =>
      captureException(err, {
        tags: {
          "issue.type": "fetch",
        },
      })
    );

export const multiYotpoReviewsAPIFetcher = (
  paramsArray: YotpoReviewsAPIParameters[]
) => {
  return Promise.all(
    paramsArray.map((params) => yotpoReviewsAPIFetcher(params))
  );
};

type LoopAPIParameters = [url: string, variantIds: ProductVariant["id"][]];

export const loopAPIFetcher = async ([url, variantIds]: LoopAPIParameters) =>
  fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Authorization": window.__LOOP_RETURNS_API_KEY__,
    },
    body: JSON.stringify({
      cart: variantIds,
    }),
  }).then((res) => res.json());

export type LegacyCartAPIParameters = [
  url: string,
  items:
    | {
        id: ProductVariant["id"];
        quantity: number;
      }[]
    | undefined,
  method?: "GET" | "POST"
];

export const legacyCartAPIFetcher = async ([
  url,
  items,
  method = "POST",
]: LegacyCartAPIParameters) => {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: items ? JSON.stringify(items) : undefined,
  }).then((res) => res.json());
};

export const legacyCartMutateAPIFetcher = async ([
  url,
  items,
  method = "POST",
]: LegacyCartAPIParameters) => {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: items ? JSON.stringify(items) : undefined,
  }).then(() => fetch(LEGACY_CART_API_URL).then((res) => res.json()));
};
